import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FiPlus, FiMinus, FiSearch, FiAlertCircle, FiLoader } from 'react-icons/fi'
import { RootState, AppDispatch } from '@/store/store'
import {
    fetchFAQs,
    searchFAQs,
    fetchFAQsByCategory,
    setActiveCategory,
    setCurrentPage,
    trackFAQView,
    markFAQHelpful
} from '@/store/slices/faqSlice'
import { fetchFAQCategories } from '@/store/slices/faqCategoriesSlice'
import { FAQ_CATEGORIES, FAQ_CATEGORY_COLORS } from '@/types/faq'

const FAQPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [searchParams, setSearchParams] = useSearchParams()
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
    const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null)
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery)

    const {
        faqs,
        isLoading,
        error,
        pagination,
        activeCategory,
        helpfulFAQs
    } = useSelector((state: RootState) => state.faq)

    const { categories, isLoading: categoriesLoading } = useSelector((state: RootState) => state.faqCategories)

    const currentPage = pagination.currentPage

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    // Update URL params when filters change
    useEffect(() => {
        const params = new URLSearchParams()
        if (debouncedSearchQuery) params.set('search', debouncedSearchQuery)
        if (activeCategory !== 'all') params.set('category', activeCategory)
        if (currentPage > 1) params.set('page', currentPage.toString())
        setSearchParams(params)
    }, [debouncedSearchQuery, activeCategory, currentPage, setSearchParams])

    // Fetch FAQ categories on mount
    useEffect(() => {
        dispatch(fetchFAQCategories())
    }, [dispatch])

    // Fetch FAQs on mount
    useEffect(() => {
        dispatch(fetchFAQs({ page: 1, limit: 10 }))
    }, [dispatch])

    // Handle search and category changes
    useEffect(() => {
        if (debouncedSearchQuery) {
            dispatch(searchFAQs({
                query: debouncedSearchQuery,
                filters: { page: 1, limit: 10, category: activeCategory !== 'all' ? activeCategory : undefined }
            }))
        } else if (activeCategory !== 'all') {
            dispatch(fetchFAQsByCategory({
                category: activeCategory,
                filters: { page: 1, limit: 10 }
            }))
        } else {
            dispatch(fetchFAQs({ page: 1, limit: 10 }))
        }
    }, [debouncedSearchQuery, activeCategory, dispatch])

    // Build categories array from fetched data
    const displayCategories = [
        { id: 'all', label: 'All FAQs', icon: undefined, color: undefined },
        ...categories.map(cat => ({
            id: cat._id,
            label: cat.name,
            icon: cat.icon,
            color: cat.color
        }))
    ]

    // Helper to get category by ID
    const getCategoryById = (id: string) => {
        return categories.find(cat => cat._id === id || cat.slug === id)
    }

    // Helper to get category color style
    const getCategoryColorClass = (categoryId: string) => {
        const slug = getCategoryById(categoryId)?.slug || categoryId
        return FAQ_CATEGORY_COLORS[slug] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }

    const toggleFAQ = (id: string) => {
        const isCurrentlyExpanded = expandedFAQ === id
        setExpandedFAQ(isCurrentlyExpanded ? null : id)

        // Track view when expanding (not when collapsing)
        if (!isCurrentlyExpanded) {
            dispatch(trackFAQView(id))
        }
    }

    const handleCategoryChange = (categoryId: string) => {
        dispatch(setActiveCategory(categoryId))
        dispatch(setCurrentPage(1))
    }

    const handlePageChange = (newPage: number) => {
        dispatch(setCurrentPage(newPage))
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handleHelpful = (faqId: string) => {
        dispatch(markFAQHelpful(faqId))
    }

    // Get category label by ID
    const getCategoryLabel = (categoryId: string) => {
        if (categoryId === 'all') return 'All FAQs'
        const cat = getCategoryById(categoryId)
        return cat?.name || FAQ_CATEGORIES[categoryId] || 'Unknown Category'
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl md:heading-hero text-gray-900 dark:text-white mb-4 sm:mb-6">Frequently Asked Questions</h1>
                    <p className="text-base sm:text-lg md:text-body-lg mb-6 sm:mb-8 text-gray-600 dark:text-gray-300">
                        Find quick answers to the most common questions about using SideGO.
                        Can't find what you're looking for?{' '}
                        <a href="/contact" className="text-blue-600 dark:text-blue-400 hover:underline">
                            Contact our support team.
                        </a>
                    </p>

                    {/* Search Bar */}
                    <div className="max-w-xl mx-auto">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search FAQs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="form-input pl-10 sm:pl-12 text-sm sm:text-base w-full py-2 sm:py-3"
                                aria-label="Search FAQs"
                            />
                            <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                                {isLoading ? (
                                    <FiLoader className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500 animate-spin" />
                                ) : (
                                    <FiSearch className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 dark:text-gray-500" />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-6 sm:py-8 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap justify-center gap-2">
                        {categoriesLoading ? (
                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                <FiLoader className="w-4 h-4 animate-spin" />
                                <span className="text-xs sm:text-sm">Loading categories...</span>
                            </div>
                        ) : (
                            displayCategories.map((category) => (
                                activeCategory === category.id ? (
                                    <button
                                        key={category.id}
                                        onClick={() => handleCategoryChange(category.id)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm transition-colors duration-200 flex items-center gap-2 whitespace-nowrap bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 font-medium"
                                        aria-pressed="true"
                                    >
                                        {category.icon && <span>{category.icon}</span>}
                                        {category.label}
                                    </button>
                                ) : (
                                    <button
                                        key={category.id}
                                        onClick={() => handleCategoryChange(category.id)}
                                        className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm transition-colors duration-200 flex items-center gap-2 whitespace-nowrap bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                                        aria-pressed="false"
                                    >
                                        {category.icon && <span>{category.icon}</span>}
                                        {category.label}
                                    </button>
                                )
                            ))
                        )}
                    </div>
                </div>
            </section>

            {/* FAQ List */}
            <section className="py-12 sm:py-16 md:py-20 bg-white dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Search/Filter Info */}
                    {(debouncedSearchQuery || activeCategory !== 'all') && !isLoading && (
                        <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                            <p className="text-sm sm:text-base md:text-body text-blue-900 dark:text-blue-100">
                                {debouncedSearchQuery && activeCategory !== 'all'
                                    ? `Found ${faqs.length} result${faqs.length !== 1 ? 's' : ''} for "${debouncedSearchQuery}" in ${getCategoryLabel(activeCategory)}`
                                    : debouncedSearchQuery
                                        ? `Found ${faqs.length} result${faqs.length !== 1 ? 's' : ''} for "${debouncedSearchQuery}"`
                                        : `Showing ${getCategoryLabel(activeCategory)} FAQs`}
                            </p>
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <div className="text-center py-12 sm:py-16">
                            <FiLoader className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 dark:text-gray-500 animate-spin mb-4" />
                            <p className="text-base sm:text-lg md:text-body text-gray-600 dark:text-gray-400">Loading FAQs...</p>
                        </div>
                    )}

                    {/* Error State */}
                    {error && (
                        <div className="text-center py-8 sm:py-12">
                            <FiAlertCircle className="w-16 h-16 sm:w-20 sm:h-20 mx-auto text-red-500 dark:text-red-400 mb-4" />
                            <h3 className="text-lg sm:text-xl md:heading-card mb-2 text-red-600 dark:text-red-400">Unable to Load FAQs</h3>
                            <p className="text-sm sm:text-base md:text-body mb-6 text-gray-600 dark:text-gray-400">
                                {error || 'There was an error loading the FAQs. Please try again later.'}
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="btn-primary"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {/* FAQ List */}
                    {!isLoading && faqs.length > 0 && (
                        <div className="space-y-3 sm:space-y-4">
                            {faqs.map((faq) => (
                                <div
                                    key={faq._id}
                                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200"
                                >
                                    {expandedFAQ === faq._id ? (
                                        <button
                                            onClick={() => toggleFAQ(faq._id)}
                                            className="w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left flex items-start justify-between hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                                            aria-expanded="true"
                                        >
                                            <div className="flex-1 mr-3 sm:mr-4">
                                                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                                                    {faq.category && (
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColorClass(faq.category)}`}>
                                                            {getCategoryLabel(faq.category)}
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="text-base sm:text-lg md:heading-card text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white text-left font-semibold">
                                                    {faq.question}
                                                </h3>
                                            </div>
                                            <div className="flex-shrink-0 ml-2">
                                                <FiMinus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
                                            </div>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => toggleFAQ(faq._id)}
                                            className="w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 text-left flex items-start justify-between hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                                            aria-expanded="false"
                                        >
                                            <div className="flex-1 mr-3 sm:mr-4">
                                                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                                                    {faq.category && (
                                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColorClass(faq.category)}`}>
                                                            {getCategoryLabel(faq.category)}
                                                        </span>
                                                    )}
                                                </div>
                                                <h3 className="text-base sm:text-lg md:heading-card text-gray-900 dark:text-white hover:text-gray-900 dark:hover:text-white text-left font-semibold">
                                                    {faq.question}
                                                </h3>
                                            </div>
                                            <div className="flex-shrink-0 ml-2">
                                                <FiPlus className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
                                            </div>
                                        </button>
                                    )}

                                    {/* Expanded Content */}
                                    {expandedFAQ === faq._id && (
                                        <div className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4">
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-3 sm:pt-4">
                                                <p className="text-sm sm:text-base md:text-body text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 whitespace-pre-wrap leading-relaxed">
                                                    {faq.answer}
                                                </p>
                                                <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 xs:gap-3">
                                                    <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
                                                        <span>👁️ {faq.views} views</span>
                                                        <span>👍 {faq.helpful} found helpful</span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleHelpful(faq._id)}
                                                        disabled={helpfulFAQs.includes(faq._id)}
                                                        className={`
                                                            flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap
                                                            ${helpfulFAQs.includes(faq._id)
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 cursor-not-allowed'
                                                                : 'bg-gray-100 hover:bg-green-100 text-gray-600 hover:text-green-800 dark:bg-gray-700 dark:hover:bg-green-900 dark:text-gray-400 dark:hover:text-green-200 cursor-pointer'
                                                            }
                                                        `}
                                                        title={helpfulFAQs.includes(faq._id) ? "You already marked this as helpful" : "Mark as helpful"}
                                                    >
                                                        👍 {helpfulFAQs.includes(faq._id) ? 'Helpful!' : 'Helpful?'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!isLoading && !error && faqs.length === 0 && (
                        <div className="text-center py-12 sm:py-16">
                            <FiSearch className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                            <h3 className="text-lg sm:text-xl md:heading-card mb-2 text-gray-900 dark:text-white">No FAQs Found</h3>
                            <p className="text-sm sm:text-base md:text-body mb-6 text-gray-600 dark:text-gray-400">
                                We couldn't find any FAQs matching your search. Try different keywords or browse all categories.
                            </p>
                            <button
                                onClick={() => {
                                    setSearchQuery('')
                                    handleCategoryChange('all')
                                }}
                                className="btn-primary"
                            >
                                Browse All FAQs
                            </button>
                        </div>
                    )}

                    {/* Pagination */}
                    {!isLoading && pagination && pagination.totalPages > 1 && (
                        <div className="mt-8 flex justify-center items-center gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                Previous
                            </button>

                            <div className="flex items-center gap-2">
                                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-3 py-2 rounded transition-colors ${currentPage === page
                                            ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900'
                                            : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                            }`}
                                        aria-current={currentPage === page ? 'page' : undefined}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === pagination.totalPages}
                                className="px-3 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Support CTA Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl md:heading-card mb-4 sm:mb-6 text-gray-900 dark:text-white">Still need help?</h2>
                    <p className="text-sm sm:text-base md:text-body mb-6 sm:mb-8 text-gray-600 dark:text-gray-400">
                        Our support team is here to assist you with any questions or issues.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                        <a href="/contact" className="btn-primary">
                            Contact Support
                        </a>
                        <a href="/help" className="btn-secondary">
                            Browse Help Center
                        </a>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default FAQPage
